import { useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Alert, Box, Button, Card, CardContent, Link, Stack, TextField, Typography } from '@mui/material';
import { resetPasswordRequest } from '../../api/auth';
import { resetPasswordSchema, ResetPasswordFormValues } from '../../validation/auth';
import { PasswordStrengthMeter } from '../../components/PasswordStrengthMeter';

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormValues>({ resolver: zodResolver(resetPasswordSchema) });

  const newPassword = watch('newPassword');

  const onSubmit = async (values: ResetPasswordFormValues) => {
    setServerError(null);
    try {
      await resetPasswordRequest(token, values.newPassword);
      setDone(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch {
      setServerError('This reset link is invalid or has expired.');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        px: 2,
      }}
    >
      <Card variant="outlined" sx={{ width: '100%', maxWidth: 400 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>
            Reset password
          </Typography>

          {!token && <Alert severity="error">No reset token was provided in the link.</Alert>}

          {done ? (
            <Alert severity="success">Password reset. Redirecting to sign in…</Alert>
          ) : (
            token && (
              <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ mt: 2 }}>
                <Stack spacing={2}>
                  {serverError && <Alert severity="error">{serverError}</Alert>}
                  <TextField
                    label="New password"
                    type="password"
                    fullWidth
                    autoFocus
                    error={!!errors.newPassword}
                    helperText={errors.newPassword?.message}
                    {...register('newPassword')}
                  />
                  <PasswordStrengthMeter password={newPassword ?? ''} />
                  <TextField
                    label="Confirm new password"
                    type="password"
                    fullWidth
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword?.message}
                    {...register('confirmPassword')}
                  />
                  <Button type="submit" variant="contained" size="large" disabled={isSubmitting} fullWidth>
                    {isSubmitting ? 'Resetting…' : 'Reset password'}
                  </Button>
                </Stack>
              </Box>
            )
          )}

          <Link component={RouterLink} to="/login" variant="body2" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
            Back to sign in
          </Link>
        </CardContent>
      </Card>
    </Box>
  );
};
